import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  List,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
  Layout,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { configurationService } from '@/services/configuration.service';

const { Title, Paragraph, Text } = Typography;
const { Sider, Content } = Layout;

const ConfigurationStudioPage: React.FC = () => {
  const qc = useQueryClient();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const overviewQ = useQuery({
    queryKey: ['ecs-overview'],
    queryFn: () => configurationService.getOverview(),
    staleTime: 30_000,
  });
  const packsQ = useQuery({
    queryKey: ['ecs-packs'],
    queryFn: () => configurationService.getSolutionPacks(),
    staleTime: 30_000,
  });
  const modulesQ = useQuery({
    queryKey: ['ecs-modules'],
    queryFn: () => configurationService.getModules(),
    staleTime: 60_000,
  });
  const featuresQ = useQuery({
    queryKey: ['ecs-features'],
    queryFn: () => configurationService.getFeatures(),
    staleTime: 60_000,
  });
  const termsQ = useQuery({
    queryKey: ['ecs-terms'],
    queryFn: () => configurationService.getTerminology(),
    staleTime: 60_000,
  });
  const menusQ = useQuery({
    queryKey: ['ecs-menus'],
    queryFn: () => configurationService.getMenus(),
    staleTime: 60_000,
  });
  const dashQ = useQuery({
    queryKey: ['ecs-dash'],
    queryFn: () => configurationService.getDashboards(),
    staleTime: 60_000,
  });
  const reportsQ = useQuery({
    queryKey: ['ecs-reports'],
    queryFn: () => configurationService.getReports(),
    staleTime: 60_000,
  });
  const formsQ = useQuery({
    queryKey: ['ecs-forms'],
    queryFn: () => configurationService.getForms(),
    staleTime: 60_000,
  });
  const wfQ = useQuery({
    queryKey: ['ecs-wf'],
    queryFn: () => configurationService.getWorkflows(),
    staleTime: 60_000,
  });
  const aiQ = useQuery({
    queryKey: ['ecs-ai'],
    queryFn: () => configurationService.getAi(),
    staleTime: 60_000,
  });
  const compQ = useQuery({
    queryKey: ['ecs-comp'],
    queryFn: () => configurationService.getCompliance(),
    staleTime: 60_000,
  });
  const locQ = useQuery({
    queryKey: ['ecs-loc'],
    queryFn: () => configurationService.getLocalization(),
    staleTime: 60_000,
  });
  const diagQ = useQuery({
    queryKey: ['ecs-diag'],
    queryFn: () => configurationService.getDiagnostics(),
    staleTime: 30_000,
  });
  const upgradeQ = useQuery({
    queryKey: ['ecs-upgrade'],
    queryFn: () => configurationService.upgradePreview(),
    staleTime: 60_000,
  });

  const packs = ((packsQ.data as any)?.packs as any[]) || [];
  const selected = useMemo(
    () => packs.find((p) => p.pack_key === selectedKey) || packs[0] || null,
    [packs, selectedKey]
  );

  const installMut = useMutation({
    mutationFn: async () => {
      const key = String(selected?.pack_key || '');
      await configurationService.install(key, false);
      return configurationService.install(key, true);
    },
    onSuccess: () => {
      message.success(
        'Solution pack install recorded — pack-gated navigation will show after refresh'
      );
      void qc.invalidateQueries({ queryKey: ['ecs-packs'] });
      void qc.invalidateQueries({ queryKey: ['ecs-overview'] });
      void qc.invalidateQueries({ queryKey: ['ecs-diag'] });
      void qc.invalidateQueries({ queryKey: ['solution-packs'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const uninstallMut = useMutation({
    mutationFn: async () => {
      const key = String(selected?.pack_key || '');
      await configurationService.uninstall(key, false);
      return configurationService.uninstall(key, true);
    },
    onSuccess: () => {
      message.success(
        'Solution pack uninstalled — pack-gated navigation is hidden; permissions and ERP data are unchanged'
      );
      void qc.invalidateQueries({ queryKey: ['ecs-packs'] });
      void qc.invalidateQueries({ queryKey: ['ecs-overview'] });
      void qc.invalidateQueries({ queryKey: ['ecs-diag'] });
      void qc.invalidateQueries({ queryKey: ['solution-packs'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const comingSoon = selected?.status === 'coming_soon';
  const installed = Boolean(selected?.installed);

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          Enterprise Configuration Studio
        </Title>
        <Tag color="blue">ECS Phase 11.5</Tag>
        {(overviewQ.data as any)?.disabled ? <Tag color="orange">ECS_JOBS_DISABLED</Tag> : null}
      </Space>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Metadata-driven Solution Packs. Uninstall hides pack-gated navigation for this company
        (Manufacturing Core / Oil &amp; Gas items). It does not revoke role permissions or delete ERP
        data; API access remains permission-based.
      </Paragraph>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            Installed: <Text strong>{String((overviewQ.data as any)?.installedCount ?? '—')}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            Catalog: <Text strong>{String((overviewQ.data as any)?.packCatalogCount ?? '—')}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            Modules: <Text strong>{String((overviewQ.data as any)?.moduleCount ?? '—')}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            Diagnostics:{' '}
            <Tag color={(overviewQ.data as any)?.diagnosticsHealth === 'ok' ? 'green' : 'gold'}>
              {String((overviewQ.data as any)?.diagnosticsHealth ?? '—')}
            </Tag>
          </Card>
        </Col>
      </Row>

      <Layout style={{ background: 'transparent', minHeight: 480 }}>
        <Sider
          width={300}
          theme="light"
          style={{ border: '1px solid #f0f0f0', borderRadius: 8, marginRight: 16, padding: 8 }}
        >
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Solution Packs
          </Text>
          <List
            size="small"
            loading={packsQ.isLoading}
            dataSource={packs}
            rowKey={(r) => String(r.pack_key)}
            renderItem={(item) => (
              <List.Item
                onClick={() => setSelectedKey(String(item.pack_key))}
                style={{
                  cursor: 'pointer',
                  background:
                    (selected?.pack_key || packs[0]?.pack_key) === item.pack_key
                      ? '#f5f5f5'
                      : undefined,
                  borderRadius: 6,
                  padding: '8px 10px',
                }}
              >
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Space wrap>
                    <Text>{item.title}</Text>
                    {item.installed ? <Tag color="green">Installed</Tag> : null}
                    {item.status === 'coming_soon' ? <Tag>Coming Soon</Tag> : null}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {item.pack_key} · v{item.current_version || '—'}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        </Sider>
        <Content>
          <Card
            title={selected?.title || 'Select a pack'}
            extra={
              <Space>
                <Button
                  type="primary"
                  disabled={!selected || comingSoon || installed}
                  loading={installMut.isPending}
                  onClick={() => installMut.mutate()}
                >
                  Install
                </Button>
                <Button
                  danger
                  disabled={!selected || !installed || comingSoon}
                  loading={uninstallMut.isPending}
                  onClick={() => uninstallMut.mutate()}
                >
                  Uninstall
                </Button>
              </Space>
            }
          >
            <Paragraph>{selected?.description || '—'}</Paragraph>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag>{selected?.status}</Tag>
              {(selected?.dependencies || []).map((d: any) => (
                <Tag key={String(d.packKey)} color="purple">
                  requires {d.packKey}
                </Tag>
              ))}
            </Space>
            <Paragraph type="secondary">
              Install/uninstall updates the company registry and pack-gated navigation. Role
              permissions and ERP data are unchanged; APIs remain permission-based.
            </Paragraph>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Tabs
              items={[
                {
                  key: 'modules',
                  label: 'Modules',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.module_key)}
                      dataSource={((modulesQ.data as any)?.modules as any[]) || []}
                      columns={[
                        { title: 'Key', dataIndex: 'module_key', width: 160 },
                        { title: 'Title', dataIndex: 'title' },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          width: 100,
                          render: (s) => <Tag>{s}</Tag>,
                        },
                      ]}
                    />
                  ),
                },
                {
                  key: 'features',
                  label: 'Features',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.feature_key)}
                      dataSource={((featuresQ.data as any)?.features as any[]) || []}
                      columns={[
                        { title: 'Feature', dataIndex: 'feature_key', width: 160 },
                        { title: 'Module', dataIndex: 'module_key', width: 140 },
                        { title: 'Title', dataIndex: 'title' },
                        {
                          title: 'Effective',
                          dataIndex: 'effective_status',
                          width: 110,
                          render: (s) => <Tag>{s}</Tag>,
                        },
                      ]}
                    />
                  ),
                },
                {
                  key: 'terminology',
                  label: 'Terminology',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => `${r.entity_key}-${r.pack_key}`}
                      dataSource={((termsQ.data as any)?.terms as any[]) || []}
                      columns={[
                        { title: 'Entity', dataIndex: 'entity_key', width: 140 },
                        { title: 'Pack', dataIndex: 'pack_key', width: 160 },
                        { title: 'Default', dataIndex: 'default_label' },
                        { title: 'Alias', dataIndex: 'alias_label' },
                      ]}
                    />
                  ),
                },
                {
                  key: 'menus',
                  label: 'Menus',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.menu_key)}
                      dataSource={((menusQ.data as any)?.menus as any[]) || []}
                      columns={[
                        { title: 'Key', dataIndex: 'menu_key', width: 180 },
                        { title: 'Title', dataIndex: 'title' },
                        { title: 'Path', dataIndex: 'path' },
                      ]}
                    />
                  ),
                },
                {
                  key: 'dashboards',
                  label: 'Dashboards',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.dashboard_key)}
                      dataSource={((dashQ.data as any)?.dashboards as any[]) || []}
                      columns={[
                        { title: 'Key', dataIndex: 'dashboard_key', width: 180 },
                        { title: 'Title', dataIndex: 'title' },
                      ]}
                    />
                  ),
                },
                {
                  key: 'reports',
                  label: 'Reports',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.report_key)}
                      dataSource={((reportsQ.data as any)?.reports as any[]) || []}
                      columns={[
                        { title: 'Key', dataIndex: 'report_key', width: 180 },
                        { title: 'Title', dataIndex: 'title' },
                        { title: 'Format', dataIndex: 'format_hint', width: 100 },
                      ]}
                    />
                  ),
                },
                {
                  key: 'forms',
                  label: 'Forms',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.form_key)}
                      dataSource={((formsQ.data as any)?.forms as any[]) || []}
                      columns={[
                        { title: 'Key', dataIndex: 'form_key', width: 180 },
                        { title: 'Title', dataIndex: 'title' },
                      ]}
                    />
                  ),
                },
                {
                  key: 'workflows',
                  label: 'Workflows',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.workflow_key)}
                      dataSource={((wfQ.data as any)?.workflows as any[]) || []}
                      columns={[
                        { title: 'Key', dataIndex: 'workflow_key', width: 220 },
                        { title: 'Title', dataIndex: 'title' },
                        { title: 'Kind', dataIndex: 'kind', width: 100 },
                      ]}
                    />
                  ),
                },
                {
                  key: 'ai',
                  label: 'AI',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.ai_pack_key)}
                      dataSource={((aiQ.data as any)?.aiPacks as any[]) || []}
                      columns={[
                        { title: 'Key', dataIndex: 'ai_pack_key', width: 160 },
                        { title: 'Title', dataIndex: 'title' },
                      ]}
                    />
                  ),
                },
                {
                  key: 'compliance',
                  label: 'Compliance',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.compliance_key)}
                      dataSource={((compQ.data as any)?.compliance as any[]) || []}
                      columns={[
                        { title: 'Key', dataIndex: 'compliance_key', width: 180 },
                        { title: 'Title', dataIndex: 'title' },
                        { title: 'Region', dataIndex: 'region', width: 100 },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          width: 120,
                          render: (s) => <Tag>{s}</Tag>,
                        },
                      ]}
                    />
                  ),
                },
                {
                  key: 'localization',
                  label: 'Localization',
                  children: (
                    <Table
                      size="small"
                      rowKey={(r) => String(r.locale_key)}
                      dataSource={((locQ.data as any)?.localization as any[]) || []}
                      columns={[
                        { title: 'Locale', dataIndex: 'locale_key', width: 100 },
                        { title: 'Language', dataIndex: 'language', width: 100 },
                        { title: 'Currency', dataIndex: 'currency', width: 90 },
                        { title: 'TZ', dataIndex: 'time_zone' },
                      ]}
                    />
                  ),
                },
                {
                  key: 'permissions',
                  label: 'Permissions',
                  children: (
                    <Paragraph type="secondary">
                      Solution Packs contribute permission metadata via registry; role assignments are
                      unchanged. Codes: configuration.view / admin / install.
                    </Paragraph>
                  ),
                },
                {
                  key: 'diagnostics',
                  label: 'Diagnostics',
                  children: (
                    <>
                      <Paragraph>
                        Health:{' '}
                        <Tag color={(diagQ.data as any)?.health === 'ok' ? 'green' : 'gold'}>
                          {String((diagQ.data as any)?.health ?? '—')}
                        </Tag>
                      </Paragraph>
                      <Table
                        size="small"
                        pagination={false}
                        rowKey={(r) => String(r.pack_key || r.pack)}
                        dataSource={((diagQ.data as any)?.installedPacks as any[]) || []}
                        columns={[
                          { title: 'Pack', dataIndex: 'pack_key' },
                          { title: 'Version', dataIndex: 'version', width: 100 },
                          { title: 'Status', dataIndex: 'status', width: 120 },
                        ]}
                      />
                      {(((diagQ.data as any)?.brokenReferences as any[]) || []).length ? (
                        <Paragraph type="danger" style={{ marginTop: 12 }}>
                          Broken deps:{' '}
                          {((diagQ.data as any).brokenReferences as any[])
                            .map((b) => `${b.pack}→${b.missing}`)
                            .join(', ')}
                        </Paragraph>
                      ) : null}
                    </>
                  ),
                },
                {
                  key: 'upgrade',
                  label: 'Upgrade',
                  children: (
                    <>
                      <Paragraph type="secondary">
                        Vendor defaults never overwrite customer overrides. Diff/merge/rollback are
                        advisory stubs.
                      </Paragraph>
                      <Table
                        size="small"
                        rowKey={(r) => `${r.entityType}-${r.entityKey}`}
                        dataSource={((upgradeQ.data as any)?.diffs as any[]) || []}
                        columns={[
                          { title: 'Type', dataIndex: 'entityType', width: 140 },
                          { title: 'Key', dataIndex: 'entityKey' },
                          { title: 'Strategy', dataIndex: 'mergeStrategy', width: 200 },
                        ]}
                        locale={{ emptyText: 'No customer overrides yet' }}
                      />
                    </>
                  ),
                },
              ]}
            />
          </Card>
        </Content>
      </Layout>
    </div>
  );
};

export default ConfigurationStudioPage;
